/**
 * Costing + BOM summary — read-only glance at the Style File Overview, one
 * row split in two: the cost build-up (left, led by the quoted FOB per piece)
 * and the bill of materials it generated (right). No actions — the cost sheet
 * is edited/locked on the Costing screen, sourced on the Sourcing screen.
 *
 * Both halves read the single `costing.get` payload the Overview already
 * fetches for the quoted-margin stat — no extra round-trip.
 *
 * @see apps/web/src/features/styles/components/costing-section.tsx — the working screen
 */
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { StatusPill } from "@flint/ui";
import type { costing as costingContract } from "@flint/api-contract";
import { formatDate, bpToPercent, paiseToMoney, milliToQty } from "../../../lib/format.js";
import { SectionCard } from "./style-cockpit-chrome.js";

type CostingData = costingContract.CostingDetailWire;

export function CostingSummaryCard({
  styleId,
  costing,
  currency,
  loading,
}: {
  styleId: string;
  costing: CostingData | null | undefined;
  currency: string;
  loading: boolean;
}) {
  const locked = costing?.status === "locked";
  // Before the costing locks there is no BOM, and a second card saying so adds
  // nothing the Costing card hasn't already said. The Style File replaces the
  // whole downstream stack with one "start here" banner in that state, so this
  // half simply doesn't render.
  const hasBom = (costing?.bomLines.length ?? 0) > 0;
  const b = costing?.breakdown;
  const money = (paise: string) => paiseToMoney(paise, currency);

  const costingAction = (
    <>
      <StatusPill tone={locked ? "ok" : costing ? "sky" : "muted"} size="sm" dot>
        {locked ? "Locked" : costing ? "Draft" : "Not started"}
      </StatusPill>
      <Link to="/merchandising/styles/$styleId/costing" params={{ styleId }} className="flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-800">
        Go to Costing <ArrowRight size={12} />
      </Link>
    </>
  );

  const bomAction = (
    <>
      <StatusPill tone={costing && costing.bomLines.length > 0 ? "ok" : "muted"} size="sm" dot>
        {costing && costing.bomLines.length > 0 ? `${costing.bomLines.length} lines` : "No BOM"}
      </StatusPill>
      <Link to="/merchandising/styles/$styleId/materials" params={{ styleId }} className="flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-800">
        Go to Sourcing <ArrowRight size={12} />
      </Link>
    </>
  );

  return (
    <div className={`grid gap-4 ${hasBom ? "lg:grid-cols-2" : ""}`}>
      {/* ── Left: the cost build-up, led by the per-piece price. ── */}
      <SectionCard
        title="Costing"
        meta={locked && costing?.lockedAt ? `Frozen ${formatDate(costing.lockedAt)}` : "Per-piece cost build-up"}
        id="sec-costing-summary"
        action={costingAction}
      >
        {loading ? (
          <p className="text-sm text-muted">Loading costing…</p>
        ) : !costing || !b ? (
          <div className="rounded-sm border border-dashed border-line bg-bg px-4 py-5">
            <p className="text-sm text-muted">No cost sheet yet.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* The headline number. */}
            <div className="rounded-md border border-line bg-bg px-4 py-3.5">
              <div className="font-mono text-3xl font-bold leading-none tracking-tight text-ink">{money(b.fobPaise)}</div>
              <div className="mt-1.5 text-xs font-semibold uppercase tracking-wider text-muted">
                Quoted FOB / piece
              </div>
              <div className="mt-2 border-t border-line pt-2 text-xs text-muted">
                Order total ({costing.orderQty.toLocaleString("en-IN")} pcs){" "}
                <span className="font-mono font-semibold text-ink-2">
                  {money((BigInt(b.fobPaise) * BigInt(costing.orderQty)).toString())}
                </span>
              </div>
            </div>

            {/* The build-up. */}
            <dl className="flex flex-col">
              <CostRow label="Fabric" value={money(b.fabricPaise)} />
              <CostRow label="Trims" value={money(b.trimsPaise)} />
              {BigInt(b.packingPaise) > 0n ? <CostRow label="Packing" value={money(b.packingPaise)} /> : null}
              {BigInt(b.processPaise) > 0n ? <CostRow label="Process / job-work" value={money(b.processPaise)} /> : null}
              <CostRow label={`CM (SAM ${(costing.samMinutesMilli / 1000).toString()} ÷ ${bpToPercent(costing.efficiencyBp)}% eff)`} value={money(b.cmPaise)} />
              <CostRow label={`Overhead (${bpToPercent(costing.overheadBp)}%)`} value={money(b.overheadPaise)} />
              <CostRow label={`Margin (${bpToPercent(costing.marginBp)}%)`} value={money(b.marginPaise)} />
            </dl>
          </div>
        )}
      </SectionCard>

      {/* ── Right: what that cost sheet says we have to buy. ── */}
      {hasBom ? (
      <SectionCard
        title="Bill of materials"
        meta={costing && costing.bomLines.length > 0 ? `Scaled to ${costing.orderQty.toLocaleString("en-IN")} pcs` : "Generated when costing locks"}
        id="sec-bom-summary"
        action={bomAction}
      >
        {loading ? (
          <p className="text-sm text-muted">Loading materials…</p>
        ) : !costing || costing.bomLines.length === 0 ? (
          <div className="rounded-sm border border-dashed border-line bg-bg px-4 py-5">
            <p className="text-sm text-muted">No materials yet — lock the costing to generate the BOM.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                  <th className="pb-2 pr-3 text-left font-semibold">Material</th>
                  <th className="whitespace-nowrap px-2 pb-2 text-right font-semibold">Per pc</th>
                  <th className="whitespace-nowrap pb-2 pl-2 text-right font-semibold">Required</th>
                </tr>
              </thead>
              <tbody>
                {costing.bomLines.map((r) => (
                  <tr key={r.id} className="border-b border-line last:border-0">
                    <td className="py-2 pr-3">
                      <div className="font-medium text-ink">{r.label}</div>
                      <div className="text-xs capitalize text-muted">{r.kind}</div>
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-right font-mono text-ink-2">
                      {milliToQty(r.perPieceMilli, r.uom)}
                    </td>
                    <td className="whitespace-nowrap py-2 pl-2 text-right font-mono font-semibold text-ink">
                      {milliToQty(Number(r.requiredQtyMilli), r.uom)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
      ) : null}
    </div>
  );
}

function CostRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line py-1.5 last:border-0">
      <dt className="min-w-0 text-xs text-muted">{label}</dt>
      <dd className="shrink-0 font-mono text-xs font-medium text-ink-2">{value}</dd>
    </div>
  );
}
